import { useCallback, useEffect, useRef, useState } from "react";
import { useDialogFocus } from "./dialogFocus";

// THE MATCHING WORKSPACE: two books and a match bar over the whole viewport, shared by the
// main and the prior-period Manual Match. Rendered IN PLACE (FullscreenTable's technique)
// so the grids keep their search, filters, pins and page across the toggle. The caller
// opens it on purpose, never on a tick: a selection click that also rearranged the screen
// would move the row under the cursor.
export function useMatchWorkspace() {
  const [workspace, setWorkspace] = useState(false);
  const [wsLayout, setWsLayout] = useState<"side" | "stacked">("side");
  const wsRef = useRef<HTMLDivElement | null>(null);
  useDialogFocus(workspace, wsRef);
  useEffect(() => {
    if (!workspace) return;
    const onKey = (e: KeyboardEvent) => {
      // A grid's own full screen or an open column filter takes Esc first.
      if (e.key === "Escape" && !document.querySelector(".fullscreen-overlay, .filter-popover")) {
        setWorkspace(false);
      }
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [workspace]);
  // Each book's grid height in the workspace: its share of the books box, less that book's
  // OWN toolbar, panels and pager as they measure right now — a live search adds a bar a
  // fixed allowance under-counts. The books box is flex-sized and a book's chrome does not
  // depend on its grid's height, so re-measuring after a resize settles immediately.
  const booksRef = useRef<HTMLDivElement | null>(null);
  const [gridHs, setGridHs] = useState<(number | undefined)[]>([undefined, undefined]);
  useEffect(() => {
    const box = booksRef.current;
    if (!workspace || !box) {
      setGridHs([undefined, undefined]);
      return;
    }
    const books = [...box.querySelectorAll<HTMLElement>(":scope > .mm-book")];
    const fit = () => {
      const share = wsLayout === "stacked"
        ? (box.clientHeight - parseFloat(getComputedStyle(box).rowGap || "0")) / 2
        : box.clientHeight;
      const next = books.map((b) => {
        const scroll = b.querySelector<HTMLElement>(".grid-scroll");
        return scroll ? Math.max(160, Math.floor(share - (b.offsetHeight - scroll.offsetHeight)))
          : undefined;
      });
      setGridHs((prev) => (prev.every((v, i) => v === next[i]) ? prev : next));
    };
    const ro = new ResizeObserver(fit);
    ro.observe(box);
    books.forEach((b) => ro.observe(b));
    return () => ro.disconnect();
  }, [workspace, wsLayout]);
  // The docked match bar and each grid's cell-selection strip (Sum, Count, Average...) both
  // dock to the bottom of the screen, and the bar covered the strip (finance 2026-09-28).
  // Its live height goes on the host as --mm-matchbar-h, which lifts the strip above it.
  const matchbarRo = useRef<ResizeObserver | null>(null);
  const matchbarRef = useCallback((el: HTMLDivElement | null) => {
    matchbarRo.current?.disconnect();
    matchbarRo.current = null;
    const host = el?.parentElement;
    if (!el || !host) return;
    const ro = new ResizeObserver(() => host.style.setProperty("--mm-matchbar-h", `${el.offsetHeight}px`));
    ro.observe(el);
    matchbarRo.current = ro;
  }, []);
  return { workspace, setWorkspace, wsLayout, setWsLayout, wsRef, booksRef, gridHs, matchbarRef };
}
